import { createSelector } from '@reduxjs/toolkit'
import { always, filter, find, isNil, pipe, propEq, when } from 'ramda'
import { SYSTEM_ADMIN_USERS } from 'app/constants'
import type { SortConfig } from 'core/helpers/createSorter'
import getDataSelector, { getByIdSelector } from 'core/utils/getDataSelector'
import { createSharedSelector, selectParamsFromProps } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { emptyObj } from 'utils/fp'
import { castBoolToStr } from 'utils/misc'
import { isFederatedUser } from '../../ssoManagement/helper'
import { isSystemUser } from './helpers'
import type { IUsersSelector } from './model'

export const credentialsSelector = getDataSelector<DataKeys.ManagementCredentials>(
  DataKeys.ManagementCredentials,
)

export const basicUsersSelector = createSharedSelector(
  getDataSelector<DataKeys.ManagementUsers>(DataKeys.ManagementUsers),
  (users) => {
    return users
  },
)

/**
 * O(1) lookup for basic users by ID from normalized cache
 * Use this for direct ID-based lookups: selectBasicUsersById(state)('user-123')
 * @example
 * const selectUserById = getByIdSelector(DataKeys.ManagementUsers)
 * const user = useAppSelector(state => selectUserById(state)('user-123'))
 */
export const basicUsersById = getByIdSelector(DataKeys.ManagementUsers)

/**
 * @deprecated Use basicUsersById for direct lookups or basicUsersSelector for list operations
 * Kept for backward compatibility - transforms list to object map
 */
export const basicUsersByIdSelector = createSharedSelector(basicUsersSelector, (users) => {
  return users.reduce((accum, user) => {
    return {
      ...accum,
      [user.id]: user,
    }
  }, {})
})

export const federatedUsersSelector = createSharedSelector(
  getDataSelector<DataKeys.FederatedDomainUsers>(DataKeys.FederatedDomainUsers),
  (users) => {
    return users
  },
)

/**
 * O(1) lookup for federated users by ID from normalized cache
 * Use this for direct ID-based lookups: selectFederatedUsersById(state)('user-123')
 */
export const federatedUsersById = getByIdSelector(DataKeys.FederatedDomainUsers)

/**
 * @deprecated Use federatedUsersById for direct lookups or federatedUsersSelector for list operations
 * Kept for backward compatibility - transforms list to object map
 */
export const federatedUsersByIdSelector = createSharedSelector(federatedUsersSelector, (users) => {
  return users.reduce((accum, user) => {
    return {
      ...accum,
      [user.id]: user,
    }
  }, {})
})

export const usersSelector = createSharedSelector(
  getDataSelector<DataKeys.ManagementUsers>(DataKeys.ManagementUsers),
  getDataSelector<DataKeys.ManagementCredentials>(DataKeys.ManagementCredentials),
  getDataSelector<DataKeys.Domains>(DataKeys.Domains),
  (rawUsers, credentials, domains) => {
    const filterSystemUsers = (user: IUsersSelector) => {
      return !isSystemUser(user) && user.username && !SYSTEM_ADMIN_USERS.includes(user.username)
    }

    // Get all tenant users and assign their corresponding tenant ID
    // const pluckUsers = map<Tenant, PluckedTenantUsers[]>((tenant) =>
    //   tenant.users.map((user) => ({
    //     ...user,
    //     tenantId: tenant.id,
    //   })),
    // )

    // Unify all users with the same ID and group the tenants
    // const unifyTenantUsers = map<PluckedTenantUsers[], UnifiedTenantUsers>(
    //   (groupedUsers: PluckedTenantUsers[]) => ({
    //     ...omit<PluckedTenantUsers, 'tenantId'>(['tenantId'], head(groupedUsers)),
    //     tenants: innerJoin(
    //       (tenant, id) => tenant.id === id,
    //       validTenants,
    //       uniq(pluck<'tenantId', PluckedTenantUsers>('tenantId', groupedUsers)),
    //     ),
    //   }),
    // )
    const allUsers = rawUsers.map((user) => ({
      ...user,
      enabled: !!user.enabled,
      id: user.id,
      username: user.name,
      displayname: user.displayname,
      email: user.email || user.name,
      defaultProject: user.default_project_id,
      options: user.options,
      // Federated users are shadow user entries created for SSO users in Keystone
      // Federated users should not be directly managed in PCD
      // And should be controlled from SSO providers and SAML groups mapping in PCD
      // Local users are the users directly managed in PCD and can be edited/deleted directly
      isLocal: user.is_local || !isFederatedUser(user),
      twoFactor: pipe(
        find(propEq('user_id', user.id)),
        when(isNil, always(emptyObj)),
        propEq('type', 'totp'),
        castBoolToStr('enabled', 'disabled'),
      )(credentials),
      mfa: {
        enabled: false,
      },
      domain: domains.find((d) => d.id === user.domain_id)?.name,
    }))
    return allUsers.filter(filterSystemUsers)

    // return pipe<
    //   Tenant[],
    //   PluckedTenantUsers[][],
    //   PluckedTenantUsers[],
    //   {
    //     [index: string]: PluckedTenantUsers[]
    //   },
    //   PluckedTenantUsers[][],
    //   UnifiedTenantUsers[],
    //   IUsersSelector[],
    //   IUsersSelector[]
    // >(
    //   // pluckUsers,
    //   flatten,
    //   groupBy(prop('id')),
    //   values,
    //   unifyTenantUsers,
    //   upsertAllBy(prop('id'), allUsers),
    //   filter(filterSystemUsers),
    // )(validTenants)
  },
)

export const localUsersSelector = createSharedSelector(usersSelector, (users) => {
  return users?.filter((user) => user.isLocal)
})

// FR-003: SSO Users tab shows only federated/SSO users
export const ssoUsersSelector = createSharedSelector(usersSelector, (users) => {
  return users?.filter((user) => !user.isLocal)
})

export const makeFilteredUsersSelector = (
  defaultParams = {} as SortConfig & { systemUsers?: boolean },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  return createSelector(usersSelector, selectParams, (users, params) => {
    const { systemUsers } = params
    const filterUsers = filter<IUsersSelector>((user) => {
      return (
        (systemUsers || !isSystemUser(user)) &&
        user.username &&
        !SYSTEM_ADMIN_USERS.includes(user.username)
      )
    })
    return pipe<IUsersSelector[], IUsersSelector[]>(filterUsers)(users)
  })
}

export const userRolesSelector = getDataSelector<DataKeys.ManagementUsersRoleAssignments>(
  DataKeys.ManagementUsersRoleAssignments,
  ['userId'],
)
